import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress, IconButton, LinearProgress, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Modal from '@Components/Modal';
import SbButton from '@Components/Button';
import {
  mapAiInvoicesToShipments,
  processVendorInvoiceExtraction,
  processBatchVendorInvoiceExtraction,
} from './services/vendorInvoiceAiService';

const MAX_FILES = 10;

interface ImportVendorInvoiceAiModalProps {
  open: boolean;
  onClose: () => void;
  onExtracted: (shipments: any[], fileName: string, fileSize: number) => void;
  acceptedFileTypes?: 'pdf_and_images' | 'all';
}

export default function ImportVendorInvoiceAiModal({
  open,
  onClose,
  onExtracted,
  acceptedFileTypes = 'pdf_and_images',
}: ImportVendorInvoiceAiModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isCancelledRef = useRef<boolean>(false);

  // Clean up any pending polling timer on unmount
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);

  // Reset state on modal open/close
  useEffect(() => {
    if (open) {
      isCancelledRef.current = false;
      setSelectedFiles([]);
      setIsProcessing(false);
      setStatusMessage('');
      setUploadProgress(0);
      setErrorMessage(null);
      setIsDragOver(false);
    } else {
      isCancelledRef.current = true;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    }
  }, [open]);

  const handleCancelAndClose = () => {
    isCancelledRef.current = true;
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsProcessing(false);
    onClose();
  };

  const validateFileType = (file: File): { isValid: boolean; error?: string } => {
    if (acceptedFileTypes === 'pdf_and_images') {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const mimeType = file.type?.toLowerCase() || '';
      const validExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
      const validMimes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
        'image/pjpeg',
        'image/x-png',
      ];

      // Check extension
      if (!validExtensions.includes(ext)) {
        return {
          isValid: false,
          error: `Invalid file type for "${file.name}". Please upload a PDF or image file (.pdf, .jpg, .jpeg, .png, .webp).`,
        };
      }

      // Check MIME type if present and not a generic binary octet-stream
      if (
        mimeType &&
        mimeType !== 'application/octet-stream' &&
        mimeType !== 'binary/octet-stream' &&
        !validMimes.includes(mimeType)
      ) {
        return {
          isValid: false,
          error: `Invalid file format for "${file.name}". Only PDF and image files (.pdf, .jpg, .jpeg, .png, .webp) are supported.`,
        };
      }
    }

    return { isValid: true };
  };

  const handleFilesSelect = (incomingFiles: File[]) => {
    if (!incomingFiles || incomingFiles.length === 0) return;

    // Rule: Reject if total files exceed MAX_FILES (strictly 10)
    if (incomingFiles.length > MAX_FILES) {
      setErrorMessage(
        `A maximum of ${MAX_FILES} files can be uploaded at a time. You selected ${incomingFiles.length} files.`,
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (selectedFiles.length + incomingFiles.length > MAX_FILES) {
      setErrorMessage(
        `A maximum of ${MAX_FILES} files can be uploaded at a time. You currently have ${selectedFiles.length} file(s) selected and attempted to add ${incomingFiles.length} more.`,
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate all incoming files
    for (const file of incomingFiles) {
      const validation = validateFileType(file);
      if (!validation.isValid) {
        setErrorMessage(validation.error || `Invalid file type for "${file.name}".`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    // Deduplicate against existing files
    const existingKeys = new Set(selectedFiles.map(f => `${f.name}_${f.size}_${f.lastModified}`));
    const uniqueFiles: File[] = [];

    for (const file of incomingFiles) {
      const key = `${file.name}_${file.size}_${file.lastModified}`;
      if (!existingKeys.has(key)) {
        existingKeys.add(key);
        uniqueFiles.push(file);
      }
    }

    if (uniqueFiles.length === 0) {
      setErrorMessage('The selected file(s) have already been added.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (selectedFiles.length + uniqueFiles.length > MAX_FILES) {
      setErrorMessage(`A maximum of ${MAX_FILES} files can be uploaded at a time.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setErrorMessage(null);
    setSelectedFiles(prev => [...prev, ...uniqueFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (indexToRemove: number) => {
    if (isProcessing) return;
    setSelectedFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
    setErrorMessage(null);
  };

  const handleClearAll = () => {
    if (isProcessing) return;
    setSelectedFiles([]);
    setErrorMessage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelect(Array.from(e.target.files));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isProcessing) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelect(Array.from(e.dataTransfer.files));
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isProcessing) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleExtract = async () => {
    if (selectedFiles.length === 0) {
      setErrorMessage('Please select at least one vendor invoice document.');
      return;
    }
    if (selectedFiles.length > MAX_FILES) {
      setErrorMessage(`A maximum of ${MAX_FILES} files can be uploaded at a time.`);
      return;
    }
    if (isProcessing) {
      return;
    }

    // Validate all files before processing
    for (const file of selectedFiles) {
      const validation = validateFileType(file);
      if (!validation.isValid) {
        setErrorMessage(validation.error || `Invalid file type for "${file.name}".`);
        return;
      }
    }

    isCancelledRef.current = false;
    setIsProcessing(true);
    setErrorMessage(null);
    setUploadProgress(0);

    try {
      // Branch 1: Single file upload (retains 100% existing flow)
      if (selectedFiles.length === 1) {
        const file = selectedFiles[0];
        const response = await processVendorInvoiceExtraction(file, {
          onStatusChange: msg => {
            if (!isCancelledRef.current) setStatusMessage(msg);
          },
          onUploadProgress: percent => {
            if (!isCancelledRef.current) setUploadProgress(percent);
          },
          isCancelled: () => isCancelledRef.current,
          registerTimer: timer => {
            pollingTimerRef.current = timer;
          },
        });

        if (isCancelledRef.current) return;

        if (response.status === 'completed') {
          if (response.fields) {
            const shipments = mapAiInvoicesToShipments(response.fields);
            if (!shipments || shipments.length === 0) {
              setErrorMessage('No valid shipment records could be extracted from this invoice.');
              return;
            }

            onExtracted(shipments, file.name, file.size);
            onClose();
          } else {
            setErrorMessage('No invoice data returned by the extraction service.');
          }
        } else if (response.status === 'needs_review') {
          const invalidFieldsMsg =
            Array.isArray(response.invalidFields) && response.invalidFields.length > 0
              ? ` (${response.invalidFields.join(', ')})`
              : '';
          setErrorMessage(
            response.error || `Invoice extraction requires review${invalidFieldsMsg}. Please enter details manually.`,
          );
        } else if (response.status === 'failed' || response.error) {
          setErrorMessage(response.error || 'Failed to extract vendor invoice details. Please try again.');
        } else {
          setErrorMessage(response.error || `Unexpected extraction status: ${response.status}`);
        }
        return;
      }

      // Branch 2: Multiple files (2 to 10 files) -> Batch extraction flow
      const batchResult = await processBatchVendorInvoiceExtraction(selectedFiles, {
        onStatusChange: msg => {
          if (!isCancelledRef.current) setStatusMessage(msg);
        },
        onUploadProgress: percent => {
          if (!isCancelledRef.current) setUploadProgress(percent);
        },
        isCancelled: () => isCancelledRef.current,
        registerTimer: timer => {
          pollingTimerRef.current = timer;
        },
      });

      if (isCancelledRef.current) return;

      if (!batchResult.shipments || batchResult.shipments.length === 0) {
        setErrorMessage(
          batchResult.error || 'No valid shipment records could be extracted from the uploaded invoices.',
        );
        return;
      }

      const totalSize = selectedFiles.reduce((acc, f) => acc + f.size, 0);
      const displayName = `${selectedFiles.length} files (${selectedFiles.map(f => f.name).join(', ')})`;

      onExtracted(batchResult.shipments, displayName, totalSize);
      onClose();
    } catch (err: any) {
      if (isCancelledRef.current) return;
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.errorDetails ||
        err?.message ||
        'Failed to process invoice documents. Please try again.';
      setErrorMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      if (!isCancelledRef.current) {
        setIsProcessing(false);
      }
    }
  };

  const acceptAttribute =
    acceptedFileTypes === 'pdf_and_images'
      ? '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp'
      : '.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.csv,.xlsx,.xls';

  const totalSizeMb = (selectedFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024)).toFixed(2);

  return (
    <Modal open={open} onClose={handleCancelAndClose} maxWidth="md">
      {/* Hidden File Input supporting multiple file selection */}
      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        accept={acceptAttribute}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" className="font-bold" sx={{ color: 'text.primary', lineHeight: 1.2 }}>
              Import with AI
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {acceptedFileTypes === 'pdf_and_images'
                ? `Upload up to ${MAX_FILES} vendor invoice documents (PDF or Image) to extract shipments and charges`
                : `Upload up to ${MAX_FILES} vendor invoice documents (PDF, Image, Excel, Word) to extract shipments and charges`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleCancelAndClose} size="small" sx={{ color: 'text.secondary' }}>
          <HighlightOffIcon />
        </IconButton>
      </Box>

      {/* Error display */}
      {errorMessage && (
        <Box
          sx={(theme: any) => ({
            mb: 3,
            p: 2,
            borderRadius: '12px',
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          })}
        >
          <ErrorOutlineIcon color="error" />
          <Typography color="error" variant="body2" className="font-medium">
            {errorMessage}
          </Typography>
        </Box>
      )}

      {/* Main Content Area */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {selectedFiles.length === 0 ? (
            /* Empty State: Full Dropzone */
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              sx={(theme: any) => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                minHeight: '220px',
                border: '2px dashed',
                borderColor: isDragOver ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.3),
                borderRadius: '16px',
                backgroundColor: isDragOver
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.primary.main, 0.02),
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease-in-out',
                opacity: isProcessing ? 0.7 : 1,
                '&:hover': {
                  borderColor: isProcessing ? undefined : theme.palette.primary.main,
                  backgroundColor: isProcessing ? undefined : alpha(theme.palette.primary.main, 0.05),
                },
              })}
            >
              <CloudUploadIcon color="primary" sx={{ fontSize: '52px', mb: 1.5 }} />
              <Typography variant="subtitle1" className="font-bold mb-1" sx={{ color: 'text.primary' }}>
                Choose Vendor Invoices to Import
              </Typography>
              <Typography variant="body2" className="text-center mb-1" sx={{ color: 'text.secondary' }}>
                Drag & drop or click to browse (up to {MAX_FILES} files)
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                {acceptedFileTypes === 'pdf_and_images'
                  ? 'Supports PDF, JPG, JPEG, PNG, or WEBP documents'
                  : 'Supports PDF, Images, Excel, Word, or CSV documents'}
              </Typography>
            </Box>
          ) : selectedFiles.length === 1 ? (
            /* Single-File State: Existing single-file preview */
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={(theme: any) => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                minHeight: '220px',
                border: '2px dashed',
                borderColor: isDragOver ? theme.palette.primary.main : theme.palette.primary.main,
                borderRadius: '16px',
                backgroundColor: isDragOver
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.success.main, 0.04),
                transition: 'all 0.2s ease-in-out',
              })}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: '48px', mb: 1 }} />
                <Typography
                  variant="subtitle1"
                  className="font-bold mb-1"
                  sx={{ color: 'text.primary', textAlign: 'center' }}
                >
                  {selectedFiles[0].name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2 }}>
                  {(selectedFiles[0].size / (1024 * 1024)).toFixed(2)} MB — {selectedFiles[0].type || 'Document'}
                </Typography>
                {!isProcessing && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Typography
                      variant="caption"
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      + Add more files (up to {MAX_FILES})
                    </Typography>
                    <Typography
                      variant="caption"
                      onClick={handleClearAll}
                      sx={{
                        color: 'error.main',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      Remove file
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            /* Multiple-Files State: Clean file list container */
            <Paper
              variant="outlined"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={(theme: any) => ({
                p: 2.5,
                borderRadius: '16px',
                borderColor: isDragOver ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.3),
                backgroundColor: isDragOver
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.background.paper, 0.8),
              })}
            >
              {/* Header: count & clear all */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 22 }} />
                  <Typography variant="subtitle2" className="font-bold" sx={{ color: 'text.primary' }}>
                    {selectedFiles.length} files selected ({totalSizeMb} MB total)
                  </Typography>
                </Box>
                {!isProcessing && (
                  <SbButton
                    variant="text"
                    color="error"
                    size="small"
                    onClick={handleClearAll}
                    sx={{ minWidth: 'auto', p: '2px 8px' }}
                  >
                    Clear all
                  </SbButton>
                )}
              </Box>

              {/* Scrollable list */}
              <Box
                sx={{ maxHeight: 200, overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                {selectedFiles.map((file, idx) => (
                  <Box
                    key={`${file.name}_${file.size}_${idx}`}
                    sx={(theme: any) => ({
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.25,
                      borderRadius: '8px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                      <InsertDriveFileOutlinedIcon color="primary" sx={{ fontSize: 20, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={file.name}
                          className="font-semibold"
                          sx={{ color: 'text.primary' }}
                        >
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                    {!isProcessing && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(idx)}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                        title="Remove file"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Add more files zone */}
              {selectedFiles.length < MAX_FILES && !isProcessing && (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={(theme: any) => ({
                    mt: 2,
                    p: 1.5,
                    border: '1px dashed',
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.06),
                      borderColor: theme.palette.primary.main,
                    },
                  })}
                >
                  <AddCircleOutlineIcon color="primary" sx={{ fontSize: 18 }} />
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Add more files ({MAX_FILES - selectedFiles.length} remaining)
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Processing indicator */}
      {isProcessing && (
        <Paper
          elevation={0}
          sx={(theme: any) => ({
            mt: 3,
            p: 2.5,
            borderRadius: '12px',
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <CircularProgress size={24} color="primary" />
            <Typography variant="body2" className="font-semibold" sx={{ color: 'text.primary' }}>
              {statusMessage || 'Processing invoice documents...'}
            </Typography>
          </Box>
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box sx={{ width: '100%', mt: 1 }}>
              <LinearProgress
                color="primary"
                variant="determinate"
                value={uploadProgress}
                sx={{ borderRadius: 4, height: 6 }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', textAlign: 'right', mt: 0.5 }}
              >
                {uploadProgress}% uploaded
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <SbButton
          variant="outlined"
          onClick={handleCancelAndClose}
          disabled={isProcessing}
          sx={{ borderRadius: '8px !important' }}
        >
          Cancel
        </SbButton>
        <SbButton
          variant="contained"
          color="primary"
          onClick={handleExtract}
          disabled={selectedFiles.length === 0 || isProcessing}
          startIcon={isProcessing ? undefined : <AutoAwesomeIcon />}
          sx={{
            borderRadius: '8px !important',
          }}
        >
          {isProcessing
            ? 'Extracting Details...'
            : selectedFiles.length > 1
            ? `Extract & Process (${selectedFiles.length} Files)`
            : 'Extract & Process'}
        </SbButton>
      </Box>
    </Modal>
  );
}
